(() => {
  'use strict';

  const ALLOWED_FIELDS = Object.freeze([
    'schemaVersion', 'text', 'fontFamily', 'colorMode', 'colorValue', 'multicolorSelection',
    'signUse', 'widthInches', 'heightInches', 'outline', 'backing', 'cut', 'mounting',
    'powerAdapter', 'remoteAndDimmer', 'discountCode'
  ]);
  const ALLOWED_COLOR_MODES = new Set(['single', 'multicolor', 'rgb']);
  const ALLOWED_USES = new Set(['indoor', 'outdoor']);
  const ALLOWED_BACKINGS = new Set(['clear', 'glossy-white', 'glossy-black', 'silver', 'gold']);
  const ALLOWED_CUTS = new Set(['cut-to-shape', 'cut-to-letter', 'cut-rectangle', 'cut-to-circle']);
  const ALLOWED_MOUNTING = new Set(['none', 'wall', 'sign']);
  const HEX = /^#[0-9a-f]{6}$/i;
  // The server rounds to whole seconds and browser clocks can differ slightly.
  // Never accept an already-expired quote; tolerate only a small future skew.
  const QUOTE_CLOCK_SKEW_MS = 5000;

  function assert(condition, message, code = 'designer_contract_invalid') {
    if (!condition) throw Object.assign(new Error(message), { code });
  }

  function buildQuoteRequest(input) {
    const request = {
      schemaVersion: 2,
      text: String(input.text || '').trim(),
      fontFamily: String(input.fontFamily || '').trim(),
      colorMode: String(input.colorMode || ''),
      colorValue: String(input.colorValue || ''),
      multicolorSelection: Array.isArray(input.multicolorSelection)
        ? input.multicolorSelection.map(entry => ({
          characterIndex: String(entry.characterIndex),
          color: String(entry.color || '').toUpperCase()
        }))
        : [],
      signUse: String(input.signUse || ''),
      widthInches: Number(input.widthInches),
      heightInches: Number(input.heightInches),
      outline: Boolean(input.outline),
      backing: String(input.backing || ''),
      cut: String(input.cut || ''),
      mounting: String(input.mounting || ''),
      powerAdapter: 'canada',
      remoteAndDimmer: true,
      ...(input.discountCode ? { discountCode: String(input.discountCode).trim().toUpperCase() } : {})
    };

    assert([...request.text].length > 0 && [...request.text].length <= 200, 'Text is required and must be 200 characters or fewer.', 'designer_text_invalid');
    assert(request.fontFamily.length > 0 && request.fontFamily.length <= 80, 'Font is invalid.');
    assert(ALLOWED_COLOR_MODES.has(request.colorMode), 'Colour mode is invalid.');
    assert(request.colorMode === 'rgb' ? request.colorValue === 'rgb' : HEX.test(request.colorValue), 'Colour is invalid.');
    assert(ALLOWED_USES.has(request.signUse), 'Sign use is invalid.');
    assert(Number.isFinite(request.widthInches) && request.widthInches >= 18 && request.widthInches <= 120, 'Width is invalid.');
    assert(Number.isFinite(request.heightInches) && request.heightInches >= 4 && request.heightInches <= 100, 'Height is invalid.');
    assert(ALLOWED_BACKINGS.has(request.backing), 'Backing is invalid.');
    assert(ALLOWED_CUTS.has(request.cut), 'Cut is invalid.');
    assert(ALLOWED_MOUNTING.has(request.mounting), 'Mounting is invalid.');
    assert(request.multicolorSelection.every(entry => /^\d+$/.test(entry.characterIndex) && HEX.test(entry.color)), 'Multicolour selection is invalid.');
    assert(request.colorMode === 'multicolor' ? request.multicolorSelection.length > 0 : request.multicolorSelection.length === 0, 'Multicolour selection does not match the colour mode.');
    assert(request.multicolorSelection.length <= 100, 'Multicolour selection is too large.');
    const textCharacters = [...request.text];
    const selectedIndexes = request.multicolorSelection.map(entry => Number(entry.characterIndex));
    assert(new Set(selectedIndexes).size === selectedIndexes.length, 'Multicolour selection contains a duplicate character.');
    assert(selectedIndexes.every(index => index >= 0 && index < textCharacters.length && textCharacters[index].trim()), 'Multicolour selection contains an invalid character.');
    assert(Object.keys(request).every(key => ALLOWED_FIELDS.includes(key)), 'Quote request contains an unsupported field.');
    return Object.freeze(request);
  }

  function normalizeQuoteResponse(data, nowMs = Date.now()) {
    const quoteId = data?.quoteId;
    const total = Number(data?.total);
    const originalTotal = Number(data?.originalTotal);
    const saleTotal = Number(data?.saleTotal);
    const couponDiscount = Number(data?.couponDiscount);
    const widthInches = Number(data?.dimensions?.widthInches);
    const heightInches = Number(data?.dimensions?.heightInches);
    const stripLengthM = Number(data?.stripLengthM);
    const expiresAt = Date.parse(data?.expiresAt || '');
    const totalCents = Number(data?.totalCents);
    const originalTotalCents = Number(data?.originalTotalCents);
    const saleTotalCents = Number(data?.saleTotalCents);
    const couponDiscountCents = Number(data?.couponDiscountCents);

    assert(data?.schemaVersion === 2, 'Quote schema is invalid.', 'quote_invalid');
    assert(typeof quoteId === 'string' && /^[0-9a-f]{32}$/.test(quoteId), 'Quote reference is invalid.', 'quote_invalid');
    assert(data?.currency === 'CAD', 'Quote currency is invalid.', 'quote_invalid');
    assert(Number.isFinite(total) && total > 0 && total <= 100000, 'Quote total is invalid.', 'quote_invalid');
    assert(Number.isFinite(originalTotal) && originalTotal >= total && originalTotal <= 100000, 'Quote list total is invalid.', 'quote_invalid');
    assert(Number.isFinite(saleTotal) && saleTotal >= total && saleTotal <= originalTotal, 'Quote sale total is invalid.', 'quote_invalid');
    assert(Number.isFinite(couponDiscount) && couponDiscount >= 0 && couponDiscount <= saleTotal, 'Quote discount is invalid.', 'quote_invalid');
    assert(Number.isFinite(widthInches) && widthInches >= 18 && widthInches <= 120, 'Quoted width is invalid.', 'quote_invalid');
    assert(Number.isFinite(heightInches) && heightInches >= 4 && heightInches <= 100, 'Quoted height is invalid.', 'quote_invalid');
    assert(Number.isFinite(stripLengthM) && stripLengthM > 0 && stripLengthM <= 1000, 'Quoted strip length is invalid.', 'quote_invalid');
    assert(Number.isFinite(expiresAt) && expiresAt > nowMs && expiresAt <= nowMs + 15 * 60 * 1000 + QUOTE_CLOCK_SKEW_MS, 'Quote has expired.', 'quote_expired');
    assert([totalCents, originalTotalCents, saleTotalCents, couponDiscountCents].every(Number.isInteger), 'Quote cents are invalid.', 'quote_invalid');
    assert(Math.round(total * 100) === totalCents, 'Quote total representations disagree.', 'quote_invalid');
    assert(Math.round(originalTotal * 100) === originalTotalCents, 'Quote list total representations disagree.', 'quote_invalid');
    assert(Math.round(saleTotal * 100) === saleTotalCents, 'Quote sale total representations disagree.', 'quote_invalid');
    assert(Math.round(couponDiscount * 100) === couponDiscountCents, 'Quote discount representations disagree.', 'quote_invalid');
    assert(saleTotalCents - couponDiscountCents === totalCents, 'Quote discount does not reconcile.', 'quote_invalid');
    assert(Array.isArray(data?.includedOptionIds), 'Quote included options are invalid.', 'quote_invalid');
    assert(data.includedOptionIds.length <= 40 && data.includedOptionIds.every(value => typeof value === 'string' && value.length > 0 && value.length <= 80), 'Quote included options are invalid.', 'quote_invalid');
    for (const value of [data.pricingVersion, data.geometryVersion, data.pricingConfigId, data.rateClass]) {
      assert(typeof value === 'string' && value.length > 0 && value.length <= 120, 'Quote metadata is invalid.', 'quote_invalid');
    }

    return Object.freeze({
      quoteId, currency: 'CAD', total, originalTotal, saleTotal, couponDiscount,
      totalCents, originalTotalCents, saleTotalCents, couponDiscountCents,
      widthInches, heightInches, stripLengthM, expiresAt,
      pricingVersion: data.pricingVersion,
      geometryVersion: data.geometryVersion,
      pricingConfigId: data.pricingConfigId,
      rateClass: data.rateClass,
      includedOptionIds: Object.freeze([...data.includedOptionIds])
    });
  }

  function priceMirrors(quote) {
    return Object.freeze({
      totalPrice: quote.originalTotal,
      discountPrice: quote.total,
      finalPrice: quote.total,
      checkoutPrice: quote.total,
      displayedPrice: quote.total
    });
  }

  window.EasyNeonDesignerPricingContractV2 = Object.freeze({
    allowedQuoteFields: ALLOWED_FIELDS,
    buildQuoteRequest,
    normalizeQuoteResponse,
    priceMirrors
  });
})();
